import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import WhoWeHelp from "@/pages/WhoWeHelp";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

const SITE_URL = "https://www.groundbreakingit.co.uk";

const ROUTE_META = {
  "/": {
    title: "IT Support Glasgow & Edinburgh | Groundbreaking IT",
    desc: "Local managed IT support, cyber security and cloud services for growing businesses across Scotland's Central Belt. Technology that just works.",
  },
  "/services": {
    title: "Managed IT, Cyber Security & Cloud | Groundbreaking IT",
    desc: "Managed IT support, cyber security, Microsoft 365, networking, backup and IT projects for small and medium businesses in the Central Belt.",
  },
  "/who-we-help": {
    title: "IT Support by Industry | Groundbreaking IT",
    desc: "How Groundbreaking IT supports professional services, healthcare practices, construction, manufacturing, logistics and charities across Scotland.",
  },
  "/about": {
    title: "About Groundbreaking IT | Local IT Support, Central Belt",
    desc: "A founder-led, honest local IT company serving Glasgow, Edinburgh and the Central Belt. Straight answers, responsive support, clear monthly agreements.",
  },
  "/faq": {
    title: "Frequently Asked Questions | Groundbreaking IT",
    desc: "Straight answers about our free IT reviews, contracts, response times, onboarding and coverage across Scotland's Central Belt.",
  },
  "/contact": {
    title: "Contact Us & Free IT Review | Groundbreaking IT",
    desc: "Get in touch with Groundbreaking IT. Call, WhatsApp or request a free 30-minute IT review for your business in Glasgow, Edinburgh or the Central Belt.",
  },
};

const setMeta = (attr, key, content) => {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = ROUTE_META[pathname] || ROUTE_META["/"];
    const url = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
    document.title = meta.title;
    setMeta("name", "description", meta.desc);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.desc);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.desc);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <BrowserRouter>
        <ScrollToTop />
        <RouteSeo />
        <div className="App min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/who-we-help" element={<WhoWeHelp />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <Footer />
          <WhatsAppFloat />
        </div>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
